import React, { useState } from 'react';
import {
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  Grid,
} from '@mui/material';
import { FilterOptions, PLAN_STAGES } from '@shared/schema';
import { useTranslation } from '@/core/i18n/i18n';
import { normalizeAnnualPlanStatusFilterOptions } from './annualPlanFilterUtils';

interface AnnualPlanFiltersProps {
  filterOptions: FilterOptions;
  onFilterChange: () => void;
  onClearFilters: () => void;
  isAnyLoading: boolean;
  filters: {
    hierarchicalModelId: string;
    status: string;
    year: string;
    fromDate: string;
    toDate: string;
    skipDefaultYear?: string;
  };
  setFilters: React.Dispatch<
    React.SetStateAction<{
      hierarchicalModelId: string;
      status: string;
      year: string;
      fromDate: string;
      toDate: string;
      skipDefaultYear?: string;
    }>
  >;
}

const AnnualPlanFilters: React.FC<AnnualPlanFiltersProps> = ({
  filterOptions,
  onFilterChange,
  onClearFilters,
  isAnyLoading,
  filters,
  setFilters,
}) => {
  const { t } = useTranslation();

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleSearch = () => {
    onFilterChange();
  };

  const handleClear = () => {
    const clearedFilters = {
      hierarchicalModelId: '',
      status: '',
      year: '',
      fromDate: '',
      toDate: '',
      skipDefaultYear: '1',
    };
    setFilters(clearedFilters);
    onClearFilters();
  };

  const getStageName = (stage: string | undefined) => {
    switch (stage) {
      case PLAN_STAGES.DEFINITION:
      case PLAN_STAGES.DEFINITION_OUT_OF_TIME:
        return t('common.definition');
      case PLAN_STAGES.SELF_ASSESSMENT:
        return t('common.self_assessment');
      default:
        return t('common.definition');
    }
  };

  const statusOptions = normalizeAnnualPlanStatusFilterOptions(filterOptions.status.slice(1));

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 1, border: '1px solid #A5B2BB' }} elevation={0}>
      {/* Primera fila - Campos usando todo el espacio */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Unidad de Negocio */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
            {t('filters.businessUnit')}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={filters.hierarchicalModelId}
              displayEmpty
              onChange={(e) => handleFilterChange('hierarchicalModelId', e.target.value)}
              data-testid="select-hierarchical-model"
            >
              <MenuItem value="">{t('common.select')}</MenuItem>
              {filterOptions.hierarchicalModels.slice(1).map((model) => (
                <MenuItem key={model.Designation} value={model.HierarchicalModelId}>
                  {model.Designation}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Status */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
            {t('filters.period')}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={filters.status}
              displayEmpty
              onChange={(e) => handleFilterChange('status', e.target.value)}
              data-testid="select-status"
            >
              <MenuItem value="">{t('common.select')}</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {getStageName(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Year */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
            {t('filters.year')}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={filters.year}
              displayEmpty
              onChange={(e) => handleFilterChange('year', e.target.value)}
              data-testid="select-year"
            >
              <MenuItem value="">{t('common.select')}</MenuItem>
              {filterOptions.years.slice(1).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Fecha */}
        <Grid size={{ xs: 12, sm: 6, md: 8 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                {t('filters.from')}
              </Typography>
              <Grid container alignItems="center" spacing={1}>
                <Grid size="grow">
                  <TextField
                    required
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    data-testid="input-fecha-desde"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                {t('filters.to')}
              </Typography>
              <Grid container alignItems="center" spacing={1}>
                <Grid size="grow">
                  <TextField
                    required
                    type="date"
                    size="small"
                    fullWidth
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    data-testid="input-fecha-hasta"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Grid container spacing={2} alignItems="flex" justifyContent={'flex-end'} marginTop={4}>
            <Grid size={{ xs: 6, sm: 6, md: 6 }}>
              <Button
                variant="outlined"
                onClick={handleClear}
                fullWidth
                size="small"
                disabled={isAnyLoading}
                sx={{
                  borderColor: '#00BCE4',
                  color: '#00BCE4',
                  borderRadius: 20,
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#00acc1',
                    bgcolor: 'rgba(0, 188, 212, 0.04)',
                  },
                }}
                data-testid="button-limpiar-filtros"
              >
                {t('filters.clearFilters')}
              </Button>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                fullWidth
                disabled={isAnyLoading}
                size="small"
                sx={{
                  bgcolor: '#00BCE4',
                  color: 'white',
                  borderRadius: 20,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#00acc1' },
                }}
                data-testid="button-buscar"
              >
                {t('common.search')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AnnualPlanFilters;
